/**
 * NetClient — the socket, and nothing else.
 *
 * Deliberately knows nothing about sharks, meshes or the scene: it owns the connection, the
 * protocol and the reconnect, and hands decoded events to whoever is listening. That
 * separation is what lets the whole feature be absent — with no relay configured, or with the
 * relay down, this object simply never fires an event and the game is exactly the
 * single-player game it was. Multiplayer here is additive, never load-bearing.
 *
 * RECONNECTION is exponential with jitter. Without the jitter, everyone who was on the reef
 * when a deploy restarts the relay comes back at the same instant, which is the one moment
 * the relay is least able to take them.
 */
import {
  PROTOCOL_VERSION,
  POSE_HZ,
  OWN_POSE_BYTES,
  BAY_ALPHABET,
  BAY_LEN,
  bayWorldSeed,
  encodePose,
  decodeSnapshot,
  normaliseBay,
  type PeerPose,
} from '../../shared/protocol.js';

export interface PeerInfo {
  id: number;
  /** index into SharkForge.SPECIES_ORDER */
  species: number;
  seed: number;
  size: number;
  age: number;
  name: string;
}

export interface BayListing {
  code: string;
  players: number;
  capacity: number;
}

export interface NetEvents {
  /** the relay accepted us; `world` is the seed every client must build the reef from */
  welcome(id: number, world: number, peers: PeerInfo[]): void;
  joined(peer: PeerInfo): void;
  left(id: number): void;
  /** @param sentAt the relay's own clock in ms — the axis peers are interpolated on */
  snapshot(sentAt: number, poses: PeerPose[]): void;
  status(connected: boolean): void;
  /** the bay would not take us and retrying will not help — it is full, or it is not a bay */
  rejected(reason: 'full' | 'bad-code' | 'version'): void;
  /** a feeder fish is in the water; its position is derived from the world seed and this id */
  feeder(id: number): void;
  /** somebody took it. `by` is a peer id, or our own id if it was us. */
  fed(id: number, by: number, name: string): void;
}

/**
 * Where the relay lives.
 *
 * SAME ORIGIN BY DEFAULT, in a production build. The server serves this bundle and the socket
 * from one port, so the page already knows the answer: whatever host it was loaded from, with
 * the scheme swapped to its websocket equivalent. That is worth more than it sounds. A
 * build-time address can be stale, can be missing, can be http where the page is https — and
 * every one of those failures presents to the player as "multiplayer is unavailable" with no
 * way to tell which. Derived from the origin, the client and the server cannot disagree.
 *
 * VITE_RELAY_URL still overrides, for a split deployment where the two are genuinely apart.
 *
 * IN DEV THERE IS NO DEFAULT, deliberately. `vite dev` is not a relay, so guessing the origin
 * there would have every dev session opening a socket to a server that will never answer and
 * retrying it forever. Point VITE_RELAY_URL at a locally running relay to test multiplayer.
 */
function relayUrl(): string {
  const explicit = (import.meta.env.VITE_RELAY_URL as string | undefined)?.trim() ?? '';
  if (explicit !== '') return explicit;
  if (import.meta.env.DEV) return '';
  if (typeof location === 'undefined') return '';
  return `${location.protocol === 'https:' ? 'wss' : 'ws'}://${location.host}`;
}

const URL = relayUrl();

/** the relay's HTTP origin, derived from its socket URL so only one thing is ever configured */
function httpBase(): string {
  return URL.replace(/^ws/, 'http').replace(/\/+$/, '');
}

/**
 * A fresh bay code.
 *
 * crypto.getRandomValues, and rejection-sampled rather than modulo-reduced. The alphabet is 28
 * characters, which does not divide 256, so `byte % 28` would make the first twelve letters
 * come up about 12% more often than the rest. Nothing breaks — but the codes are the one thing
 * in this game a player reads aloud and types back in, and a biased generator is how two
 * strangers end up in each other's bay.
 */
export function makeBayCode(): string {
  const n = BAY_ALPHABET.length;
  const limit = 256 - (256 % n);
  const buf = new Uint8Array(BAY_LEN * 2);
  let out = '';
  while (out.length < BAY_LEN) {
    crypto.getRandomValues(buf);
    for (let i = 0; i < buf.length && out.length < BAY_LEN; i++) {
      if (buf[i] < limit) out += BAY_ALPHABET[buf[i] % n];
    }
  }
  return out;
}

/**
 * Ask the relay which public bays have people in them.
 *
 * Never throws and never rejects: an unreachable relay means an empty list, and the landing
 * page treats that identically to nobody being online. A landing page that can fail to load is
 * a landing page that stops people playing at all.
 */
export async function listBays(signal?: AbortSignal): Promise<BayListing[]> {
  if (URL.length === 0) return [];
  try {
    const res = await fetch(`${httpBase()}/bays`, { signal });
    if (!res.ok) return [];
    const body = (await res.json()) as { bays?: BayListing[] };
    return (body.bays ?? []).filter((b) => normaliseBay(b.code) === b.code);
  } catch {
    return [];
  }
}

export class NetClient {
  readonly enabled = URL.length > 0;
  /** our own id once welcomed, 0 while not connected */
  id = 0;
  /** the shared world seed, or 0 until the relay tells us */
  world = 0;
  /** the bay we are in, '' when solo */
  bay = '';

  private ws: WebSocket | null = null;
  private readonly h: Partial<NetEvents> = {};
  private readonly poseBuf = new ArrayBuffer(OWN_POSE_BYTES);
  private lastSend = 0;
  private retry = 0;
  private timer: number | null = null;
  private closed = false;
  private species = 0;
  private seed = 1;
  private size = 1;
  private age = 0.2;
  private name = '';
  private listed = false;

  on<K extends keyof NetEvents>(k: K, fn: NetEvents[K]): void {
    this.h[k] = fn;
  }

  get connected(): boolean {
    return this.ws !== null && this.ws.readyState === WebSocket.OPEN && this.id !== 0;
  }

  /**
   * @param shark  identifies OUR animal; every peer rebuilds it from exactly these numbers
   * @param bay    which room to join. No bay, no connection — solo play never opens a socket.
   * @param listed whether this bay appears in the public list, which only its host sets
   */
  connect(
    shark: { species: number; seed: number; size: number; age: number; name: string },
    bay: string, listed = false,
  ): void {
    this.species = shark.species;
    this.seed = shark.seed >>> 0;
    this.size = shark.size;
    this.age = shark.age;
    this.name = shark.name;
    this.bay = normaliseBay(bay);
    this.listed = listed;
    if (!this.enabled || this.closed || this.bay === '') return;
    this.open();
  }

  /** what the reef seed WILL be for a bay, without waiting for a socket to say so */
  static seedForBay(code: string): number {
    const c = normaliseBay(code);
    return c === '' ? 0 : bayWorldSeed(c);
  }

  private open(): void {
    let ws: WebSocket;
    try {
      ws = new WebSocket(URL);
    } catch {
      this.scheduleRetry();
      return;
    }
    ws.binaryType = 'arraybuffer';
    this.ws = ws;

    ws.onopen = () => {
      this.retry = 0;
      ws.send(JSON.stringify({
        t: 'hello', v: PROTOCOL_VERSION,
        species: this.species, seed: this.seed,
        size: this.size, age: this.age, name: this.name,
        bay: this.bay, listed: this.listed,
      }));
    };

    ws.onmessage = (ev) => {
      if (ev.data instanceof ArrayBuffer) {
        const snap = decodeSnapshot(ev.data);
        this.h.snapshot?.(snap.sentAt, snap.poses);
        return;
      }
      let msg: Record<string, unknown>;
      try { msg = JSON.parse(String(ev.data)) as Record<string, unknown>; } catch { return; }
      switch (msg.t) {
        case 'welcome':
          this.id = msg.id as number;
          this.world = msg.world as number;
          if (typeof msg.bay === 'string') this.bay = msg.bay;
          // A feeder already in the water when we arrived, and who is wearing the prize.
          if (typeof msg.feederHolder === 'number' && msg.feederHolder !== 0) {
            this.h.fed?.(0, msg.feederHolder, '');
          }
          if (typeof msg.feeder === 'number' && msg.feeder !== 0) {
            this.h.feeder?.(msg.feeder);
          }
          this.h.welcome?.(this.id, this.world, (msg.peers as PeerInfo[]) ?? []);
          this.h.status?.(true);
          break;
        case 'feeder':
          this.h.feeder?.(msg.id as number);
          break;
        case 'fed':
          this.h.fed?.(msg.id as number, msg.by as number, (msg.name as string) ?? '');
          break;
        case 'joined':
          this.h.joined?.(msg as unknown as PeerInfo);
          break;
        case 'left':
          this.h.left?.(msg.id as number);
          break;
      }
    };

    const down = (ev?: CloseEvent): void => {
      if (this.ws !== ws) return;
      this.ws = null;
      if (this.id !== 0) this.h.status?.(false);
      this.id = 0;
      /* Some closes are answers, not failures. A full bay or a rejected code will be rejected
         identically forever, so retrying is not resilience — it is a client hammering a relay
         that has already told it no. Report it and stop. */
      const code = ev?.code ?? 0;
      if (code === 4001) { this.h.rejected?.('full'); return; }
      if (code === 1008) { this.h.rejected?.('bad-code'); return; }
      if (code === 1002) { this.h.rejected?.('version'); return; }
      this.scheduleRetry();
    };
    ws.onclose = down;
    ws.onerror = () => down();
  }

  private scheduleRetry(): void {
    if (this.closed || this.timer !== null) return;
    // Exponential to 20 s, plus up to a second of jitter: a relay restart otherwise brings
    // every client back in the same tick, which is precisely when it can least afford them.
    const wait = Math.min(20_000, 700 * Math.pow(1.8, this.retry++)) + Math.random() * 1000;
    this.timer = window.setTimeout(() => {
      this.timer = null;
      this.open();
    }, wait);
  }

  /**
   * Publish our pose, rate-limited to POSE_HZ. Called every frame; sends twelve times a
   * second. Doing the limiting here rather than at the call site means the caller never has
   * to know the rate, and the rate can change with the protocol.
   */
  sendPose(now: number, x: number, y: number, z: number, heading: number, speed: number): void {
    if (!this.connected) return;
    if (now - this.lastSend < 1 / POSE_HZ) return;
    this.lastSend = now;
    encodePose(this.poseBuf, { x, y, z, heading, speed });
    this.ws?.send(this.poseBuf);
  }

  /** claim the feeder. The relay decides; this is a request, not an assertion. */
  claim(id: number): void {
    if (!this.connected) return;
    this.ws?.send(JSON.stringify({ t: 'claim', id }));
  }

  dispose(): void {
    this.closed = true;
    if (this.timer !== null) window.clearTimeout(this.timer);
    this.timer = null;
    this.ws?.close();
    this.ws = null;
  }
}
